import React, { useEffect, useRef } from "react";
import { bool, func, number, oneOfType, string } from "prop-types";

import Spinner from "pages/_components/Spinner";

/**
 * Sentinel basado en IntersectionObserver para disparar la carga de la
 * siguiente pagina en listados con scroll infinito. Funciona sin importar que
 * elemento del DOM (window, view-content, transition-item, etc.) sea el que
 * efectivamente scrollea, lo que lo hace robusto frente a layouts diferentes
 * en desktop/tablet/mobile.
 */
function InfiniteScrollSentinel({ hasMore, isLoading, onLoadMore, rootMargin, threshold }) {
    const sentinelRef = useRef(null);

    useEffect(() => {
        if (!hasMore) {
            return undefined;
        }
        const node = sentinelRef.current;
        if (!node || typeof IntersectionObserver === "undefined") {
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !isLoading) {
                        onLoadMore();
                    }
                });
            },
            { rootMargin, threshold },
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, [hasMore, isLoading, onLoadMore, rootMargin, threshold]);

    if (!hasMore) {
        return null;
    }

    return (
        <div ref={sentinelRef} className="d-flex justify-content-center my-2">
            {isLoading && <Spinner />}
        </div>
    );
}

InfiniteScrollSentinel.propTypes = {
    hasMore: bool.isRequired,
    isLoading: bool,
    onLoadMore: func.isRequired,
    rootMargin: string,
    threshold: oneOfType([number, string]),
};

InfiniteScrollSentinel.defaultProps = {
    isLoading: false,
    rootMargin: "200px",
    threshold: 0,
};

export default InfiniteScrollSentinel;
